import * as React from 'react';

import Dropdown from 'af-components/Controls/Dropdown';

import styles from './styles.module.scss';

interface AdditionalFilterOption {
	id: number | null | string;
	label: string;
}

interface Props {
	options: AdditionalFilterOption[] | null;
	onChange: (options: AdditionalFilterOption[]) => void;
	selected: AdditionalFilterOption[];
	defaultValue?: number;
	/** Define how to call the filtering option will be referred to */
	filterName: string;
	onRemoveFilter?: () => void;
	/** Outer apperance of dropdown needs to be different if it's for bulk modal */
	forBulkModal: boolean;
	/** Should the dropdown menu be opened immediately? */
	autoOpen: boolean;
	/** Enabling this adds "None" as the first option on the list. Will send "null" as requested value of the filter */
	hasNoneAsOption?: true;
	/** Define how you'd like to display a single option within the multiselect dropdown */
	renderOptionOverride?: (option: AdditionalFilterOption) => React.JSX.Element;
	onLazyLoad?: (loaded?: boolean) => Promise<void>;
}

const AdditionalFilter: React.FC<Props> = (props) => {
	const {
		options,
		onChange,
		defaultValue,
		selected,
		filterName,
		onRemoveFilter,
		forBulkModal,
		autoOpen,
		hasNoneAsOption,
		renderOptionOverride,
		onLazyLoad,
	} = props;

	const allOptions = React.useMemo(() => {
		if (options === null) {
			// we want to make sure we're displaying loading indicator
			return null;
		} else if (hasNoneAsOption) {
			const noneAsOption: AdditionalFilterOption = { id: null, label: 'N/A' };
			return [noneAsOption, ...(options ?? [])];
		} else {
			return options;
		}
	}, [options, hasNoneAsOption]);

	const renderOption = React.useCallback((option: AdditionalFilterOption) => {
		const isNoneAsOption = option.id === null;

		const className = isNoneAsOption
			? `${styles.dropdownOption} ${styles.noneOption}`
			: styles.dropdownOption;

		return (
			<div className={className}>
				{
					!!renderOptionOverride
						? renderOptionOverride(option)
						: option.label
				}
			</div>
		);
	}, [renderOptionOverride]);

	const onValueChange = React.useCallback((_options: AdditionalFilterOption[]) => {
		onChange(_options);
	}, [onChange]);

	const defaultOption = React.useMemo(() => {
		if (!allOptions) {
			return null;
		}

		const _option = allOptions.find((option) => option.id === defaultValue);
		return _option ?? allOptions[0];
	}, [defaultValue, allOptions]);

	const placeholder = React.useMemo(() => {
		const valuePrefix = forBulkModal ? '' : `${filterName}: `;
		if (selected.length === 0) {
			return <div className={styles.emptyFilterContent}>{valuePrefix}All</div>;
		}

		const [first, ...rest] = selected;
		return (
			<div className={styles.appliedFilterContent}>
				{valuePrefix}{first?.label ?? 'Empty'}
				{rest.length > 0 && (
					<div className={styles.appliedFilterCounter}>
						{` + ${rest.length}`}
					</div>
				)}
			</div>
		);
	}, [filterName, forBulkModal, selected]);

	const containerStyle = React.useMemo(() => {
		let applicableStyle: string = '';
		if (forBulkModal) {
			applicableStyle = styles.bulkModalStyle;
		} else if (!!selected.length) {
			applicableStyle = styles.appliedFilter;
		} else {
			applicableStyle = styles.emptyFilter;
		}
		return [
			styles.additionalFilter,
			applicableStyle,
		].join(' ');
	}, [selected, forBulkModal]);

	const filterBy = React.useCallback((option: AdditionalFilterOption, rawInput: string) => {
		return option.label.toLowerCase().includes(rawInput.toLowerCase());
	}, []);

	return (
		<Dropdown<AdditionalFilterOption>
			autoOpen={!forBulkModal && autoOpen}
			containerClassName={containerStyle}
			defaultSelections={selected}
			defaultValue={defaultOption}
			dropdownId={`accqa__additional-filter-multiselect__${filterName}`}
			filterable={true}
			filterBy={filterBy}
			fixed={forBulkModal}
			forcePlaceholder={true}
			isMultiselect={true}
			onClose={forBulkModal ? undefined : onRemoveFilter}
			onLazyLoad={onLazyLoad}
			onMultiselectChange={onValueChange}
			options={allOptions}
			placeholder={placeholder}
			renderMenuItem={renderOption}
			valueKey="id"
			withCaret={true}
		/>
	);
};

export default React.memo(AdditionalFilter);
